(() => {
  const state = window.PVRMediaReconciler || {};

  const findProductInfo = (root, sectionId) => {
    if (!root || typeof root.querySelectorAll !== 'function' || !sectionId) return null;

    return Array.from(root.querySelectorAll('product-info[data-pvr-parent]')).find(
      (node) => node.dataset.section === sectionId
    );
  };

  const replaceOwnedNode = (currentNode, nextNode) => {
    if (!currentNode || !nextNode) return;
    currentNode.replaceWith(document.importNode(nextNode, true));
  };

  const syncMediaLayout = (productInfo, nextProductInfo) => {
    const currentLayout = productInfo.querySelector('.product');
    const nextLayout = nextProductInfo.querySelector('.product');
    if (!currentLayout || !nextLayout) return;

    ['product--no-media', 'grid--2-col-tablet'].forEach((className) =>
      currentLayout.classList.toggle(className, nextLayout.classList.contains(className))
    );
  };

  const syncModelJson = (productInfo, nextProductInfo) => {
    productInfo.querySelectorAll('script[type="application/json"][id^="ProductJSON-"]').forEach((node) => node.remove());

    const nextModelJson = nextProductInfo.querySelector(
      'script[type="application/json"][id^="ProductJSON-"]'
    );
    if (!nextModelJson) return;

    productInfo.append(document.importNode(nextModelJson, true));
    window.ProductModel?.loadShopifyXR?.();
  };

  const syncVisibility = (productInfo, nextProductInfo, idPrefix) => {
    const id = `${idPrefix}-${productInfo.dataset.section}`;
    const currentNode = Array.from(productInfo.querySelectorAll('[id]')).find((node) => node.id === id);
    const nextNode = Array.from(nextProductInfo.querySelectorAll('[id]')).find((node) => node.id === id);
    if (currentNode && nextNode) currentNode.classList.toggle('hidden', nextNode.classList.contains('hidden'));
  };

  const reconcileProductMedia = (productInfo, responseDocument) => {
    const sectionId = productInfo?.dataset?.section;
    const nextProductInfo = findProductInfo(responseDocument, sectionId);
    if (!productInfo || !nextProductInfo) return;

    const currentGallery = productInfo.querySelector('media-gallery');
    const nextGallery = nextProductInfo.querySelector('media-gallery');
    const currentModal = productInfo.querySelector('product-modal');
    const nextModal = nextProductInfo.querySelector('product-modal');
    if (!currentGallery || !nextGallery || !currentModal || !nextModal) return;

    syncMediaLayout(productInfo, nextProductInfo);
    replaceOwnedNode(currentGallery, nextGallery);
    replaceOwnedNode(currentModal, nextModal);
    syncModelJson(productInfo, nextProductInfo);

    ['Volume-Note', 'Price-Per-Item', 'Volume'].forEach((idPrefix) =>
      syncVisibility(productInfo, nextProductInfo, idPrefix)
    );
  };

  state.reconcileProductMedia = reconcileProductMedia;
  window.PVRMediaReconciler = state;
  if (!Object.hasOwn(state, 'pendingSelectFocus')) state.pendingSelectFocus = null;

  const clearPendingSelectFocus = () => {
    if (state.pendingSelectFocusTimer) window.clearTimeout(state.pendingSelectFocusTimer);
    state.pendingSelectFocusTimer = null;
    state.pendingSelectFocus = null;
  };

  const nativePvrSelectContext = (control) => {
    if (control?.tagName !== 'SELECT') return null;
    const variantSelects = control.closest?.('variant-selects');
    const productInfo = variantSelects?.closest?.('product-info[data-pvr-parent]');
    if (!productInfo?.dataset?.section || !control.id) return null;
    return { productInfo, sectionId: productInfo.dataset.section };
  };

  const armSelectFocus = (control, sectionId) => {
    clearPendingSelectFocus();
    const pendingFocus = { sectionId, controlId: control.id, originalControl: control };
    state.pendingSelectFocus = pendingFocus;
    state.pendingSelectFocusTimer = window.setTimeout(() => {
      if (state.pendingSelectFocus === pendingFocus) clearPendingSelectFocus();
    }, 30000);
  };

  if (!state.focusCaptureInstalled) {
    state.focusCaptureInstalled = true;
    state.captureChangedSelect = (event) => {
      const select = event?.target;
      const context = nativePvrSelectContext(select);
      if (!context) return;
      armSelectFocus(select, context.sectionId);
    };
    state.captureFocusedControl = (event) => {
      if (!state.pendingSelectFocus) return;

      const control = event?.target;
      const context = nativePvrSelectContext(control);
      if (context?.sectionId === state.pendingSelectFocus.sectionId) {
        armSelectFocus(control, context.sectionId);
      } else {
        clearPendingSelectFocus();
      }
    };
    document.addEventListener('change', state.captureChangedSelect, true);
    document.addEventListener('focusin', state.captureFocusedControl, true);
  }

  if (
    !state.subscribed &&
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined' &&
    PUB_SUB_EVENTS?.variantChange
  ) {
    state.subscribed = true;
    state.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const data = event?.data;
      if (!data?.sectionId) return;

      const pendingMatchesSection = state.pendingSelectFocus?.sectionId === data.sectionId;
      const hasValidDocument = data.html?.nodeType === 9 && typeof data.html.querySelectorAll === 'function';
      if (!hasValidDocument) {
        if (pendingMatchesSection) clearPendingSelectFocus();
        return;
      }

      const productInfo = findProductInfo(document, data.sectionId);
      if (!productInfo) {
        if (pendingMatchesSection) clearPendingSelectFocus();
        return;
      }

      reconcileProductMedia(productInfo, data.html);

      const pendingFocus = state.pendingSelectFocus;
      if (pendingFocus?.sectionId !== data.sectionId) return;

      const activeElement = document.activeElement;
      const focusWasDisplaced =
        !activeElement || activeElement === document.body || activeElement === pendingFocus.originalControl;
      const nextControl = document.getElementById(pendingFocus.controlId);
      if (focusWasDisplaced && nextControl && productInfo.contains(nextControl)) {
        nextControl.focus({ preventScroll: true });
      }
      clearPendingSelectFocus();
    });
  }
})();
